<template>
  <div class="ai-call-history">
    <div class="d-flex flex-wrap align-items-center justify-content-between mb-1">
      <div>
        <p class="ai-setup-question mb-25">Call history</p>
        <p class="ai-setup-hint mb-0">
          Time, candidate, status, and duration for this job.
        </p>
      </div>
      <b-button
        variant="outline-secondary"
        class="mt-50 mt-sm-0 ai-call-history__refresh"
        :disabled="loading"
        title="Refresh call history"
        aria-label="Refresh call history"
        @click="loadCalls"
      >
        <b-spinner v-if="loading" small />
        <feather-icon v-else icon="RefreshCwIcon" size="16" />
      </b-button>
    </div>

    <div v-if="error" class="ai-call-history__error mb-1">
      <feather-icon icon="AlertCircleIcon" size="18" class="flex-shrink-0 mr-50" />
      <div>
        <p class="ai-call-history__error-title mb-0">{{ error }}</p>
        <p v-if="notDeployed" class="ai-call-history__error-hint mb-0">
          List endpoint may not be available on this environment yet.
        </p>
      </div>
    </div>

    <b-overlay :show="loading && !rows.length" rounded spinner-small>
      <div v-if="loading && !rows.length" class="ai-call-history__loading">
        Loading call history…
      </div>
      <b-table
        v-else-if="rows.length"
        :items="rows"
        :fields="fields"
        responsive
        hover
        class="ai-call-history__table mb-0"
        @row-clicked="onRowClicked"
      >
        <template #cell(timeLabel)="data">
          <span>{{ data.item.timeLabel }}</span>
        </template>
        <template #cell(candidateLabel)="data">
          <span class="ai-call-history__name">{{ data.item.candidateLabel }}</span>
          <span v-if="data.item.candidateId" class="d-block ai-call-history__id">
            {{ data.item.candidateId }}
          </span>
        </template>
        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.item.status)" class="ai-status-pill text-capitalize">
            {{ data.item.status || 'unknown' }}
          </b-badge>
          <span v-if="data.item.outcomeLabel" class="d-block ai-call-history__outcome">
            {{ data.item.outcomeLabel }}
          </span>
        </template>
        <template #cell(durationLabel)="data">
          <span>{{ data.item.durationLabel }}</span>
        </template>
      </b-table>

      <div v-else-if="!loading && !error" class="ai-call-history__empty">
        <feather-icon icon="PhoneOffIcon" size="24" class="ai-call-history__empty-icon" />
        <p class="ai-call-history__empty-title">No calls yet</p>
        <p class="mb-0">Place a test call or start calling candidates to see history here.</p>
      </div>
    </b-overlay>

    <b-modal
      v-model="showDetail"
      title="AI call detail"
      size="md"
      ok-only
      ok-title="Close"
      body-class="py-1"
    >
      <b-overlay :show="loadingDetail" rounded spinner-small>
        <div v-if="detail" class="ai-call-history__detail">
          <p class="mb-75">
            <span class="ai-call-history__detail-label">Status</span>
            <b-badge :variant="statusVariant(detail.status)" class="ml-50 ai-status-pill text-capitalize">
              {{ detail.status || 'unknown' }}
            </b-badge>
          </p>
          <p class="mb-75">
            <span class="ai-call-history__detail-label">Candidate</span>
            <span class="d-block font-weight-bold">{{ detail.candidateLabel }}</span>
          </p>
          <p class="mb-75">
            <span class="ai-call-history__detail-label">When</span>
            <span class="d-block">{{ detail.timeLabel }}</span>
          </p>
          <p class="mb-75">
            <span class="ai-call-history__detail-label">Duration</span>
            <span class="d-block">{{ detail.durationLabel }}</span>
          </p>
          <p v-if="detail.outcomeLabel" class="mb-75">
            <span class="ai-call-history__detail-label">Outcome</span>
            <span class="d-block">{{ detail.outcomeLabel }}</span>
          </p>
          <p v-if="detailTranscript" class="mb-0">
            <span class="ai-call-history__detail-label">Transcript</span>
            <span class="d-block mt-25 ai-call-history__transcript">{{ detailTranscript }}</span>
          </p>
          <p v-else-if="!loadingDetail" class="mb-0 ai-call-history__detail-label">
            No transcript available for this call.
          </p>
        </div>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BModal,
  BOverlay,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import aiCallingService, {
  getAiCallingErrorMessage,
  isAiCallingNotDeployed,
  normalizeAiCallRow,
  parseAiCallsListResponse,
} from '@/services/ai-calling-service'

export default {
  name: 'AiCallingCallHistory',
  components: {
    BAlert,
    BBadge,
    BButton,
    BModal,
    BOverlay,
    BSpinner,
    BTable,
  },
  props: {
    jobId: {
      type: [String, Number],
      required: true,
    },
    candidates: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      error: '',
      notDeployed: false,
      rows: [],
      fields: [
        { key: 'timeLabel', label: 'Time' },
        { key: 'candidateLabel', label: 'Candidate' },
        { key: 'status', label: 'Status' },
        { key: 'durationLabel', label: 'Duration' },
      ],
      showDetail: false,
      loadingDetail: false,
      detail: null,
      detailTranscript: '',
    }
  },
  watch: {
    jobId(id) {
      if (id != null && id !== '') this.loadCalls()
    },
  },
  mounted() {
    // Parent mounts this only when Call history is expanded (lazy).
    if (this.jobId != null && this.jobId !== '') this.loadCalls()
  },
  methods: {
    statusVariant(status) {
      const s = String(status || '').toLowerCase()
      if (s === 'completed' || s === 'done' || s === 'success') return 'light-success'
      if (s === 'failed' || s === 'error') return 'light-danger'
      if (s === 'missed' || s === 'no-answer' || s === 'no_answer' || s === 'busy') return 'light-warning'
      if (s === 'initiated' || s === 'calling' || s === 'ringing' || s === 'in_progress') return 'light-info'
      if (s === 'scheduled' || s === 'queued') return 'light-secondary'
      return 'light-secondary'
    },
    async loadCalls() {
      if (this.jobId == null || this.jobId === '') return
      this.loading = true
      this.error = ''
      this.notDeployed = false
      try {
        const res = await aiCallingService.listJobAiCalls(this.jobId, { limit: 50 })
        const parsed = parseAiCallsListResponse(res)
        if (!parsed.ok) {
          this.rows = []
          this.notDeployed = !!parsed.notDeployed
          this.error = parsed.error || getAiCallingErrorMessage(res, 'ai-calls')
          return
        }
        const normalized = parsed.calls.map(row => normalizeAiCallRow(row, this.candidates))
        normalized.sort((a, b) => {
          const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0
          const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0
          return tb - ta
        })
        this.rows = normalized
      } catch (err) {
        this.rows = []
        this.error = err?.message || 'Could not load AI call history.'
      } finally {
        this.loading = false
      }
    },
    async onRowClicked(item) {
      if (!item || !item.id) return
      this.showDetail = true
      this.detail = item
      this.detailTranscript = item.raw?.transcript || ''
      this.loadingDetail = true
      try {
        const res = await aiCallingService.getAiCall(item.id)
        if (isAiCallingNotDeployed(res) || res?.status === 'FAILED' || res?.status === 'ERROR') {
          return
        }
        const raw = res?.data && typeof res.data === 'object' && !Array.isArray(res.data)
          ? res.data
          : res
        const next = normalizeAiCallRow(raw, this.candidates)
        this.detail = { ...next, id: next.id || item.id }
        this.detailTranscript = raw?.transcript || next.raw?.transcript || this.detailTranscript || ''
      } catch (e) {
        // Keep list-row detail if fetch fails
      } finally {
        this.loadingDetail = false
      }
    },
  },
}
</script>

<style scoped>
.ai-call-history {
  margin-top: 0;
}
.ai-setup-question {
  font-size: 16px;
  font-weight: 700;
  color: #2f2b3d;
}
.ai-setup-hint {
  font-size: 14px;
  color: #6e6b7b;
}
.ai-call-history__refresh {
  min-width: 2.5rem;
  min-height: 2.5rem;
  box-shadow: none;
}
.ai-call-history__table {
  font-size: 14px;
  color: #4b465c;
}
.ai-call-history__table ::v-deep thead th {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #2f2b3d;
  background: #f8f8f8;
  padding: 0.65rem 0.85rem;
  border-color: rgba(47, 43, 61, 0.12);
}
.ai-call-history__table ::v-deep tbody td {
  padding: 0.7rem 0.85rem;
  vertical-align: middle;
  border-color: rgba(47, 43, 61, 0.1);
}
.ai-call-history__table ::v-deep tbody tr {
  cursor: pointer;
}
.ai-call-history__table ::v-deep tbody tr:nth-child(even) {
  background: rgba(0, 172, 153, 0.03);
}
.ai-call-history__name {
  font-weight: 600;
  color: #2f2b3d;
}
.ai-call-history__id {
  font-size: 12px;
  color: #6e6b7b;
  font-family: inherit;
}
.ai-call-history__outcome {
  margin-top: 0.3rem;
  font-size: 13px;
  color: #6e6b7b;
}
.ai-status-pill {
  font-size: 12px;
  font-weight: 600;
  padding: 0.3em 0.65em;
  border-radius: 999px;
}
.ai-call-history__empty,
.ai-call-history__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2rem 1.25rem;
  border: 1px dashed rgba(47, 43, 61, 0.16);
  border-radius: 0.5rem;
  background: #f8f8f8;
  color: #6e6b7b;
  font-size: 14px;
}
.ai-call-history__empty-icon {
  color: #00ac99;
  margin-bottom: 0.45rem;
}
.ai-call-history__empty-title {
  font-size: 15px;
  font-weight: 600;
  color: #2f2b3d;
  margin-bottom: 0.25rem;
}
.ai-call-history__error {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem 0.9rem;
  border-radius: 0.5rem;
  background: #fdeaea;
  color: #ea5455;
  font-size: 14px;
}
.ai-call-history__error-title {
  font-weight: 600;
}
.ai-call-history__error-hint {
  margin-top: 0.25rem;
  color: #6e6b7b;
}
.ai-call-history__detail {
  font-size: 15px;
  color: #4b465c;
}
.ai-call-history__detail-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #6e6b7b;
  margin-bottom: 0.15rem;
}
.ai-call-history__transcript {
  white-space: pre-wrap;
  max-height: 12rem;
  overflow-y: auto;
  background: #f8f8f8;
  border-radius: 0.4rem;
  padding: 0.7rem 0.85rem;
  font-size: 14px;
  line-height: 1.5;
  color: #4b465c;
}
</style>
